import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
} from "@mui/material";
import { Map } from "leaflet";
import { useTranslation } from "react-i18next";
import { round } from "lodash-es";

import { Measure, MeasureConf } from "types/types";
import {
  applyPointLayerHighlightStyle,
  applyPointLayerStyle,
  applyPolygonLayerHighlightStyle,
  applyPolygonLayerStyle,
} from "utils/GeomanUtils";

import { RenderMeasureConfigs } from "components/measures/addMeasurePage/RenderMeasureConfigs";
import { StyledMarkdown } from "components/utils/StyledMarkdown";

interface Props {
  measureConfigs: MeasureConf[];
  selectedMeasure: Measure;
  mapRef: Map;
}

export function UploadedMeasureConfigs(props: Props) {
  const { measureConfigs, selectedMeasure, mapRef } = props;
  const { t } = useTranslation();

  function highlightLayers(measureConfig: MeasureConf) {
    measureConfig.layers.forEach((layer) => {
      if (measureConfig.measure.geometryType === "POLYGON") {
        return applyPolygonLayerHighlightStyle(
          layer.geometryLayer,
          measureConfig
        );
      }
      return applyPointLayerHighlightStyle(layer.geometryLayer, measureConfig);
    });
  }
  function resetLayersStyle(measureConfig: MeasureConf) {
    measureConfig.layers.forEach((layer) => {
      if (measureConfig.measure.geometryType === "POLYGON") {
        return applyPolygonLayerStyle(layer.geometryLayer, measureConfig);
      }
      return applyPointLayerStyle(layer.geometryLayer, measureConfig);
    });
  }

  return (
    <>
      {/* <Alert severity="warning">
        <AlertTitle>{t("label.measureWarnings")}</AlertTitle>
      </Alert> */}
      <StyledTableContainer>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{t("label.measures")}</TableCell>
              {selectedMeasure.measurePropertyLinkList.map((property) => (
                <TableCell key={property.id}>
                  <StyledMarkdown content={property.label} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {measureConfigs.map((measureConfig) => {
              return (
                <TableRow key={measureConfig.id}>
                  <TableCell className="color-box-cell">
                    <Box
                      onMouseEnter={() => highlightLayers(measureConfig)}
                      onMouseLeave={() => resetLayersStyle(measureConfig)}
                      className="color-box"
                      sx={{ backgroundColor: measureConfig.measure.color }}
                    ></Box>
                    {measureConfig.measure.name}
                  </TableCell>
                  {measureConfig.propertyValueList.map((property) => {
                    return (
                      <TableCell key={property.id}>
                        {`${round(property.value, 2).toLocaleString("nl-BE")} ${
                          property.unit
                        }`}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </StyledTableContainer>
      <RenderMeasureConfigs measureConfigs={measureConfigs} mapRef={mapRef} />
    </>
  );
}

const StyledTableContainer = styled(TableContainer)`
  .color-box {
    width: ${({ theme }) => theme.spacing(8)};
    min-width: ${({ theme }) => theme.spacing(8)};
    height: ${({ theme }) => theme.spacing(3)};
    display: inline-block;
    box-sizing: border-box;

    &:hover {
      border: 2.5px solid red;
    }
  }
  .color-box-cell {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(2)};
  }
  .MuiTableCell-head {
    font-weight: 700;
    p {
      font-weight: 700;
      margin-bottom: 0;
    }
  }
  .MuiTableCell-root {
    border-bottom: unset;
    padding: ${({ theme }) => theme.spacing(1.5, 2)};
  }
`;
